/**
 * LFPDPPP 동의 관리 API
 *
 * 멕시코 LFPDPPP (Ley Federal de Protección de Datos Personales en Posesión
 * de los Particulares) 컴플라이언스를 위한 회원 동의 상태 조회 / 갱신.
 *
 * - Primary (필수): 서비스 운영 / 결제 처리 / ARCO 권리 — 미동의 시 서비스 차단.
 * - Secondary (선택): 마케팅 / 통계 — 옵트인.
 *
 * @see admin#1361 (메타) / admin#1365 (BE consents 테이블 + ConsentService)
 * @see #372 (FE 하위)
 */

import { ApiClientError, apiClient } from './client';
import type {
  ConsentStatusResponse,
  MarketingConsentNotice,
  MarketingConsentProjection,
  MarketingConsentResponse,
  MarketingConsentStatus,
  PartnerVisibilityListResponse,
  PartnerVisibilityNotice,
  PartnerVisibilityRecordResponse,
  PartnerVisibilityStatus,
  PartnerVisibilityStore,
  RecordConsentRequest,
  RecordConsentResponse,
  UpdateMarketingConsentRequest,
} from './types';

const CONSENT_ENDPOINT = '/api/v1/customer/consents';
const MARKETING_CONSENT_ENDPOINT = `${CONSENT_ENDPOINT}/marketing`;
const PARTNER_VISIBILITY_ENDPOINT = `${CONSENT_ENDPOINT}/partner-visibility`;
const INVALID_MARKETING_CONSENT_RESPONSE = 'Invalid marketing consent response';
const INVALID_PARTNER_VISIBILITY_RESPONSE = 'Invalid partner visibility response';
const MAX_STORES_PER_PAGE = 50;

interface PartnerVisibilityRecordRequest {
  store_id: number;
  status: PartnerVisibilityStatus;
  via: 'settings';
}

export class MarketingNoticeStaleError extends Error {
  public readonly currentNotice: MarketingConsentNotice;

  constructor(currentNotice: MarketingConsentNotice) {
    super('Marketing notice changed');
    this.name = 'MarketingNoticeStaleError';
    this.currentNotice = Object.freeze({
      version: currentNotice.version,
      url: currentNotice.url,
    });
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  expectedKeys: readonly string[]
): boolean {
  const actualKeys = Object.keys(value);
  return (
    actualKeys.length === expectedKeys.length &&
    expectedKeys.every((key) =>
      Object.prototype.hasOwnProperty.call(value, key)
    )
  );
}

function hasStringLength(value: unknown, maxLength: number): value is string {
  return typeof value === 'string' && Array.from(value).length <= maxLength;
}

function isVersion(value: unknown): value is string {
  return (
    hasStringLength(value, 32) &&
    value.length > 0 &&
    value === value.trim()
  );
}

function isMarketingVersion(value: unknown): value is string {
  if (!isVersion(value) || value === 'system-withdrawal') {
    return false;
  }

  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 0x20 || codePoint === 0x7f) {
      return false;
    }
  }

  return true;
}

function isNormalizedLegalPath(path: string): boolean {
  if (
    !path.startsWith('/legal/') ||
    path.startsWith('//') ||
    path.includes('?') ||
    path.includes('#') ||
    path.includes('\\')
  ) {
    return false;
  }

  for (const character of path) {
    const codePoint = character.codePointAt(0) ?? 0;

    if (codePoint <= 0x20 || codePoint === 0x7f) {
      return false;
    }
  }

  return path.split('/').every((segment) => segment !== '.' && segment !== '..');
}

function isSafeLegalUrl(value: unknown): value is string {
  if (
    !hasStringLength(value, 255) ||
    /%(?![0-9A-Fa-f]{2})/u.test(value) ||
    !isNormalizedLegalPath(value)
  ) {
    return false;
  }

  let decoded: string;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    return false;
  }

  const rawSlashCount = value.split('/').length;
  const decodedSlashCount = decoded.split('/').length;

  return (
    !/%[0-9A-Fa-f]{2}/u.test(decoded) &&
    decodedSlashCount === rawSlashCount &&
    isNormalizedLegalPath(decoded)
  );
}

function isRfc3339DateTime(value: unknown): value is string {
  if (!hasStringLength(value, 40)) {
    return false;
  }

  const match =
    /^(\d{4})-(\d{2})-(\d{2})[Tt](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:[Zz]|([+-])(\d{2}):(\d{2}))$/u.exec(
      value
    );

  if (!match) {
    return false;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = match[8] === undefined ? 0 : Number(match[8]);
  const offsetMinute = match[9] === undefined ? 0 : Number(match[9]);

  if (
    month < 1 ||
    month > 12 ||
    hour > 23 ||
    minute > 59 ||
    second > 60 ||
    offsetHour > 23 ||
    offsetMinute > 59
  ) {
    return false;
  }

  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [
    31,
    isLeapYear ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];

  return day >= 1 && day <= daysInMonth[month - 1];
}

function isNotice(value: unknown): value is PartnerVisibilityNotice {
  return (
    isObject(value) &&
    isVersion(value.version) &&
    isSafeLegalUrl(value.url)
  );
}

function isMarketingNotice(value: unknown): value is MarketingConsentNotice {
  return (
    isObject(value) &&
    hasExactKeys(value, ['version', 'url']) &&
    isMarketingVersion(value.version) &&
    isSafeLegalUrl(value.url)
  );
}

function isMarketingConsentStatus(
  value: unknown
): value is MarketingConsentStatus {
  return value === 'granted' || value === 'withdrawn';
}

function isMarketingConsentProjection(
  value: unknown,
  notice: MarketingConsentNotice | null,
  allowBlockedRecordedWithdrawal = false
): value is MarketingConsentProjection {
  if (
    !isObject(value) ||
    !hasExactKeys(value, [
      'recorded_status',
      'effective_status',
      'consent_version',
      'status_changed_at',
      'can_grant',
      'can_withdraw',
    ])
  ) {
    return false;
  }

  const {
    recorded_status: recordedStatus,
    effective_status: effectiveStatus,
    consent_version: consentVersion,
    status_changed_at: statusChangedAt,
    can_grant: canGrant,
    can_withdraw: canWithdraw,
  } = value;

  if (
    (recordedStatus !== null && !isMarketingConsentStatus(recordedStatus)) ||
    !isMarketingConsentStatus(effectiveStatus) ||
    (consentVersion !== null && !isMarketingVersion(consentVersion)) ||
    (statusChangedAt !== null && !isRfc3339DateTime(statusChangedAt)) ||
    typeof canGrant !== 'boolean' ||
    typeof canWithdraw !== 'boolean'
  ) {
    return false;
  }

  if (notice === null) {
    if (recordedStatus === null) {
      return (
        effectiveStatus === 'withdrawn' &&
        consentVersion === null &&
        statusChangedAt === null &&
        canGrant === false &&
        canWithdraw === false
      );
    }

    return (
      recordedStatus === 'withdrawn' &&
      effectiveStatus === 'withdrawn' &&
      statusChangedAt !== null &&
      canGrant === false &&
      canWithdraw === false
    );
  }

  if (recordedStatus === null) {
    return (
      effectiveStatus === 'withdrawn' &&
      consentVersion === null &&
      statusChangedAt === null &&
      ((canGrant === true && canWithdraw === true) ||
        (canGrant === false && canWithdraw === false))
    );
  }

  if (statusChangedAt === null) {
    return false;
  }

  if (recordedStatus === 'granted') {
    if (consentVersion === null) {
      return false;
    }

    const current = consentVersion === notice.version;
    if (current) {
      return (
        (
          (effectiveStatus === 'granted' && canGrant === false) ||
          (effectiveStatus === 'withdrawn' &&
            typeof canGrant === 'boolean')
        ) &&
        canWithdraw === true
      );
    }

    return (
      effectiveStatus === 'withdrawn' &&
      canGrant === true &&
      canWithdraw === true
    );
  }

  return (
    effectiveStatus === 'withdrawn' &&
    canWithdraw === false &&
    (canGrant === true ||
      (allowBlockedRecordedWithdrawal && canGrant === false))
  );
}

function isMarketingConsentResponse(
  value: unknown,
  allowBlockedRecordedWithdrawal = false
): value is MarketingConsentResponse {
  if (
    !isObject(value) ||
    !hasExactKeys(value, ['success', 'data']) ||
    value.success !== true ||
    !isObject(value.data) ||
    !hasExactKeys(value.data, ['notice', 'consent'])
  ) {
    return false;
  }

  const notice =
    value.data.notice === null || isMarketingNotice(value.data.notice)
      ? value.data.notice
      : undefined;

  return (
    notice !== undefined &&
    isMarketingConsentProjection(
      value.data.consent,
      notice,
      allowBlockedRecordedWithdrawal
    )
  );
}

function isMarketingPostWriteResponse(
  value: unknown,
  requestedStatus: MarketingConsentStatus
): value is MarketingConsentResponse {
  if (!isMarketingConsentResponse(value, requestedStatus === 'withdrawn')) {
    return false;
  }

  const { consent, notice } = value.data;
  if (requestedStatus === 'granted') {
    return (
      consent.recorded_status === 'granted' &&
      (consent.effective_status === 'granted' ||
        consent.effective_status === 'withdrawn') &&
      consent.status_changed_at !== null &&
      notice !== null &&
      consent.consent_version === notice.version
    );
  }

  if (consent.effective_status !== 'withdrawn') {
    return false;
  }

  return (
    (consent.recorded_status === 'withdrawn' &&
      consent.status_changed_at !== null) ||
    (consent.recorded_status === null &&
      consent.consent_version === null &&
      consent.status_changed_at === null &&
      consent.can_grant === false &&
      consent.can_withdraw === false)
  );
}

function invalidMarketingConsentResponse(): Error {
  return new Error(INVALID_MARKETING_CONSENT_RESPONSE);
}

function staleMarketingNoticeFrom(
  error: unknown,
  requestedStatus: MarketingConsentStatus
): MarketingConsentNotice | null {
  if (
    requestedStatus !== 'granted' ||
    !(error instanceof ApiClientError) ||
    error.status !== 409 ||
    error.code !== 'marketing_notice_stale' ||
    !isObject(error.body) ||
    !hasExactKeys(error.body, ['success', 'code', 'message', 'data']) ||
    error.body.success !== false ||
    error.body.code !== 'marketing_notice_stale' ||
    typeof error.body.message !== 'string' ||
    !isObject(error.body.data) ||
    !hasExactKeys(error.body.data, ['notice']) ||
    !isMarketingNotice(error.body.data.notice)
  ) {
    return null;
  }

  return error.body.data.notice;
}

function isPartnerVisibilityStatus(value: unknown): value is PartnerVisibilityStatus {
  return value === 'granted' || value === 'withdrawn';
}

function isStoreFields(
  value: unknown,
  notice: PartnerVisibilityNotice
): value is PartnerVisibilityStore {
  if (!isObject(value)) {
    return false;
  }

  const {
    store_id: storeId,
    store_name: storeName,
    availability,
    recorded_status: recordedStatus,
    effective_status: effectiveStatus,
    consent_version: consentVersion,
    consented_at: consentedAt,
    can_grant: canGrant,
    can_withdraw: canWithdraw,
  } = value;

  if (
    !Number.isSafeInteger(storeId) ||
    (storeId as number) <= 0 ||
    !hasStringLength(storeName, 120) ||
    storeName.trim().length === 0 ||
    (availability !== 'active' && availability !== 'historical') ||
    (recordedStatus !== null && !isPartnerVisibilityStatus(recordedStatus)) ||
    !isPartnerVisibilityStatus(effectiveStatus) ||
    (consentVersion !== null && !isVersion(consentVersion)) ||
    (consentedAt !== null && !isRfc3339DateTime(consentedAt)) ||
    typeof canGrant !== 'boolean' ||
    typeof canWithdraw !== 'boolean'
  ) {
    return false;
  }

  if (recordedStatus === null) {
    return (
      availability === 'active' &&
      effectiveStatus === 'withdrawn' &&
      consentVersion === null &&
      consentedAt === null
    );
  }

  if (consentVersion === null || consentedAt === null) {
    return false;
  }

  const shouldBeGranted =
    recordedStatus === 'granted' && consentVersion === notice.version;

  return effectiveStatus === (shouldBeGranted ? 'granted' : 'withdrawn');
}

function hasGetCapabilityMatrix(store: PartnerVisibilityStore): boolean {
  if (store.availability === 'historical') {
    return (
      store.recorded_status === 'granted' &&
      store.can_grant === false &&
      store.can_withdraw === true
    );
  }

  if (store.effective_status === 'granted') {
    return store.can_grant === false && store.can_withdraw === true;
  }

  const hasGrantedEvidence = store.recorded_status === 'granted';

  return (
    typeof store.can_grant === 'boolean' &&
    store.can_withdraw === hasGrantedEvidence
  );
}

function isListResponse(value: unknown): value is PartnerVisibilityListResponse {
  if (
    !isObject(value) ||
    value.success !== true ||
    !isObject(value.data)
  ) {
    return false;
  }

  const data = value.data;

  if (
    !isNotice(data.notice) ||
    !Array.isArray(data.stores) ||
    data.stores.length > MAX_STORES_PER_PAGE ||
    !(
      data.next_cursor === null ||
      hasStringLength(data.next_cursor, 512)
    )
  ) {
    return false;
  }

  const seenStoreIds = new Set<number>();
  const responseNotice = data.notice;

  return data.stores.every((store) => {
    if (
      !isStoreFields(store, responseNotice) ||
      !hasGetCapabilityMatrix(store) ||
      seenStoreIds.has(store.store_id)
    ) {
      return false;
    }

    seenStoreIds.add(store.store_id);
    return true;
  });
}

function hasPostWriteMatrix(
  store: PartnerVisibilityStore,
  requestedStatus: PartnerVisibilityStatus
): boolean {
  if (requestedStatus === 'granted') {
    return (
      store.availability === 'active' &&
      store.can_grant === false &&
      store.can_withdraw === true
    );
  }

  if (store.availability === 'historical') {
    return store.can_grant === false && store.can_withdraw === false;
  }

  return typeof store.can_grant === 'boolean' && store.can_withdraw === false;
}

function isRecordResponse(
  value: unknown,
  request: PartnerVisibilityRecordRequest
): value is PartnerVisibilityRecordResponse {
  if (
    !isObject(value) ||
    value.success !== true ||
    !isObject(value.data) ||
    !isNotice(value.data.notice) ||
    !isStoreFields(value.data.store, value.data.notice)
  ) {
    return false;
  }

  const store = value.data.store;

  return (
    store.store_id === request.store_id &&
    store.recorded_status === request.status &&
    store.effective_status === request.status &&
    store.consented_at !== null &&
    store.consent_version === value.data.notice.version &&
    hasPostWriteMatrix(store, request.status)
  );
}

function invalidPartnerVisibilityResponse(): Error {
  return new Error(INVALID_PARTNER_VISIBILITY_RESPONSE);
}

export const consentApi = {
  /**
   * 현재 사용자 동의 상태 조회.
   *
   * BE 응답의 `needs_reconsent` flag 가 true 이면 FE 는 재동의 배너를
   * 강제 모달로 표시해야 한다 (다른 액션 차단).
   *
   * GET /api/v1/customer/consents
   */
  getStatus: async (signal?: AbortSignal): Promise<ConsentStatusResponse> => {
    return apiClient.get<ConsentStatusResponse>(CONSENT_ENDPOINT, { signal });
  },

  /**
   * 동의 기록 / 갱신.
   *
   * 회원가입 직후 BE 가 자동으로 PRIMARY 를 기록하므로 (admin#1361 hook),
   * FE 는 SECONDARY 옵트인 시 단독 호출 + 재동의 배너에서 토글 시 호출한다.
   *
   * POST /api/v1/customer/consents
   */
  record: async (request: RecordConsentRequest): Promise<RecordConsentResponse> => {
    return apiClient.post<RecordConsentResponse>(CONSENT_ENDPOINT, request);
  },

  /**
   * 계정 전역 SECONDARY 마케팅 동의 상태 조회.
   * capability 및 notice를 포함한 전체 projection을 fail-closed 검증한다.
   */
  getMarketingConsent: async (
    signal?: AbortSignal
  ): Promise<MarketingConsentResponse> => {
    const response = await apiClient.get<unknown>(MARKETING_CONSENT_ENDPOINT, {
      signal,
    });

    if (!isMarketingConsentResponse(response)) {
      throw invalidMarketingConsentResponse();
    }

    return response;
  },

  /**
   * 계정 전역 SECONDARY 마케팅 동의 변경.
   * 정확한 두 필드만 전송하고 요청 방향과 일치하는 post-write evidence만 수용한다.
   */
  updateMarketingConsent: async (
    request: UpdateMarketingConsentRequest,
    signal?: AbortSignal
  ): Promise<MarketingConsentResponse> => {
    const body: Readonly<UpdateMarketingConsentRequest> = Object.freeze({
      status: request.status,
      observed_notice_version: request.observed_notice_version,
    });
    let response: unknown;
    try {
      response = await apiClient.put<unknown>(
        MARKETING_CONSENT_ENDPOINT,
        body,
        { signal }
      );
    } catch (error: unknown) {
      const currentNotice = staleMarketingNoticeFrom(error, body.status);
      if (currentNotice !== null) {
        throw new MarketingNoticeStaleError(currentNotice);
      }
      throw error;
    }

    if (!isMarketingPostWriteResponse(response, body.status)) {
      throw invalidMarketingConsentResponse();
    }

    return response;
  },

  /**
   * 완료 주문이 있는 exact Store별 파트너 제공 동의 상태 조회.
   * eligibility/capability는 서버 응답만 사용하고 전체 응답을 fail-closed 검증한다.
   */
  getPartnerVisibility: async (
    cursor?: string,
    signal?: AbortSignal
  ): Promise<PartnerVisibilityListResponse> => {
    const endpoint =
      cursor === undefined
        ? PARTNER_VISIBILITY_ENDPOINT
        : `${PARTNER_VISIBILITY_ENDPOINT}?cursor=${encodeURIComponent(cursor)}`;
    const response = await apiClient.get<unknown>(endpoint, { signal });

    if (!isListResponse(response)) {
      throw invalidPartnerVisibilityResponse();
    }

    return response;
  },

  /**
   * exact Store 동의/철회 기록. 요청과 서버의 post-write evidence가 정확히
   * 일치할 때만 검증된 상태를 반환한다.
   */
  recordPartnerVisibility: async (
    request: PartnerVisibilityRecordRequest,
    signal?: AbortSignal
  ): Promise<PartnerVisibilityRecordResponse> => {
    const body: Readonly<PartnerVisibilityRecordRequest> = Object.freeze({
      store_id: request.store_id,
      status: request.status,
      via: 'settings',
    });
    const response = await apiClient.post<unknown>(
      PARTNER_VISIBILITY_ENDPOINT,
      body,
      { signal }
    );

    if (!isRecordResponse(response, body)) {
      throw invalidPartnerVisibilityResponse();
    }

    return response;
  },
};
